import { useEffect, useMemo, useState } from "react";
import { createRole, fetchPermissions } from "../../api/accessControl";

export default function AddRoleModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;

    let active = true;

    const loadPermissions = async () => {
      try {
        setLoadingPermissions(true);
        const data = await fetchPermissions();
        if (!active) return;
        setPermissions(Array.isArray(data) ? data : []);
      } catch {
        if (!active) return;
        setPermissions([]);
      } finally {
        if (active) setLoadingPermissions(false);
      }
    };

    loadPermissions();

    return () => {
      active = false;
    };
  }, [open]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setSelectedPermissionIds([]);
    setPermissionSearch("");
    setFormError("");
  };

  const togglePermission = (id) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const filteredPermissions = useMemo(() => {
    return permissions.filter((p) =>
      p.code.toLowerCase().includes(permissionSearch.toLowerCase())
    );
  }, [permissions, permissionSearch]);

  const submit = async () => {
    setFormError("");

    if (!name.trim() || !description.trim() || selectedPermissionIds.length === 0) {
      setFormError("Please fill in all fields and select at least one permission.");
      return;
    }

    try {
      setLoading(true);

      await createRole({
        name: name.trim(),
        description: description.trim(),
        permissionIds: selectedPermissionIds,
      });

      onCreated?.();
      onClose?.();
      resetForm();
    } catch (e) {
      setFormError("Failed to create role.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 backdrop-blur-sm">
      <div className="min-h-full px-4 py-6 sm:py-10 flex items-start justify-center">
        <div className="flex w-full max-w-2xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 max-h-[calc(100vh-3rem)]">
          {/* Header */}
          <div className="shrink-0 flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5 dark:border-slate-800">
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Create New Role
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Define a role profile and assign the relevant permissions.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              aria-label="Close"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-xl leading-none text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              ×
            </button>
          </div>

          {/* Scrollable body */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div className="space-y-6">
              {formError && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                  {formError}
                </div>
              )}

              <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-950/30">
                <div className="mb-5">
                  <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                    Role Details
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Enter a clear role name and description.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-5">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Role Name
                    </label>
                    <input
                      placeholder="e.g. SUPER_ADMIN"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
                    />
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      Use uppercase with underscores for consistency.
                    </p>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Description
                    </label>
                    <textarea
                      placeholder="Describe what this role can do"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="min-h-[110px] w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
                    />
                  </div>
                </div>
              </section>

              <section className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                      Assign Permissions
                    </h3>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Search and select the permissions this role should have.
                    </p>
                  </div>

                  <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                    {selectedPermissionIds.length} selected
                  </div>
                </div>

                <div className="mb-4">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
                      🔎
                    </span>
                    <input
                      type="text"
                      placeholder="Search permissions..."
                      value={permissionSearch}
                      onChange={(e) => setPermissionSearch(e.target.value)}
                      className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
                    />
                  </div>
                </div>

                <div className="max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50/50 p-2 dark:border-slate-800 dark:bg-slate-950/30">
                  {loadingPermissions ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-700 dark:border-slate-700 dark:border-t-slate-200" />
                      <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
                        Loading permissions...
                      </p>
                    </div>
                  ) : filteredPermissions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-xl dark:bg-slate-800">
                        📭
                      </div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        No matching permissions
                      </p>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        Try a different search term.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredPermissions.map((p) => {
                        const isChecked = selectedPermissionIds.includes(p.id);

                        return (
                          <label
                            key={p.id}
                            className={`flex cursor-pointer items-center justify-between rounded-2xl border px-4 py-3 transition ${
                              isChecked
                                ? "border-slate-900 bg-slate-50 dark:border-white dark:bg-slate-800/60"
                                : "border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-800 dark:hover:bg-slate-800/40"
                            }`}
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => togglePermission(p.id)}
                                className="h-4 w-4 rounded border-slate-300 text-black focus:ring-2 focus:ring-slate-300 dark:border-slate-600 dark:bg-slate-900 dark:text-white dark:focus:ring-slate-700"
                              />
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-slate-900 dark:text-white">
                                  {p.code}
                                </p>
                                {p.description && (
                                  <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                                    {p.description}
                                  </p>
                                )}
                              </div>
                            </div>

                            {isChecked && (
                              <span className="ml-3 shrink-0 rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-white dark:bg-white dark:text-black">
                                Selected
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 flex flex-col-reverse gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:justify-end dark:border-slate-800 bg-white dark:bg-slate-900">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={submit}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-black"
            >
              {loading && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent dark:border-black/70 dark:border-t-transparent" />
              )}
              {loading ? "Saving..." : "Create Role"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}